/**
 * mahnwesen — Mahnung PDF (kurumsal şablon) + E-Mail gövdesi.
 *
 * Kademeler: Zahlungserinnerung (ücretsiz, 3 gün) → 1./2. Mahnung
 * (%8 netto Gebühr, 7 gün) → Inkasso-Ankündigung.
 */
import {
  fetchPdfSettings, downloadPdfFromHtml, wrapPdfDocument,
  renderCompanyHeader, renderFooter, escapeHtml, formatDate,
} from '@/lib/pdfRenderer';
import { base44 } from '@/api/base44Client';
import { markMahnungEmailSent } from '@/lib/buchhaltungApi';
import { getVorlage, renderPlatzhalter, textZuHtml } from '@/lib/dokumentVorlagen';

const eur = (v) => Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const FRIST_TAGE = { 0: 3, 1: 7, 2: 7, 3: 0 };
const GEBUEHR_PROZENT = { 0: 0, 1: 8, 2: 8, 3: 0 };

/** Platzhalter-Kontext einer Mahnung. */
function mahnungKontext(m, settings = {}) {
  const level = Number(m.level_no) || 0;
  return {
    'kunde.name': m.partner_name || '', 'kunde.nummer': (m.lead_id || '').substring(0, 4).toUpperCase(),
    'firma.name': settings.company_name, 'firma.geschaeftsfuehrer': settings.geschaeftsfuehrer, 'firma.ansprechpartner': settings.ansprechpartner,
    'beleg.nummer': m.invoice_no || '', 'beleg.datum': formatDate(m.created_at || new Date()),
    'rechnung.nummer': m.invoice_no || '', 'rechnung.datum': m.invoice_date ? formatDate(m.invoice_date) : '',
    'mahnung.frist_datum': m.due_date ? formatDate(m.due_date) : '',
    'mahnung.frist_tage': m.frist_tage ?? FRIST_TAGE[level] ?? '',
    'mahnung.gebuehr_prozent': m.gebuehr_prozent ?? GEBUEHR_PROZENT[level] ?? '',
  };
}

function vorlageFuer(m, settings) {
  const level = Math.min(3, Math.max(0, Number(m.level_no) || 0));
  return getVorlage(settings, `mahnung_${level}`);
}

/** Gövde HTML (Vorschau + Download) – Texte aus der Dokumentvorlage. */
export function buildMahnungBody(m, settings) {
  const v = vorlageFuer(m, settings);
  const ctx = mahnungKontext(m, settings);
  const rows = [
    ['Offener Rechnungsbetrag (brutto)', eur(m.open_amount)],
    ...(Number(m.total_fees) > 0 ? [['Mahngebühren (kumuliert)', eur(m.total_fees)]] : []),
    ['Zu zahlender Gesamtbetrag', eur(m.total_due)],
  ];
  return `
    ${renderCompanyHeader(settings)}
    <div style="margin-bottom:20px">
      <h1 style="font-size:20px;margin:0 0 4px 0;color:#b45309">${escapeHtml(m.level_name || '').toUpperCase()}</h1>
      <div style="color:#666;font-size:11px">Datum: ${formatDate(m.created_at || new Date())} · Rechnung ${escapeHtml(m.invoice_no || '')}</div>
    </div>
    <div style="font-size:12px;font-weight:700;margin-bottom:14px">${escapeHtml(m.partner_name || '')}</div>
    <p style="font-size:11px;line-height:1.65">${textZuHtml(v.anrede, ctx)}<br/><br/>${textZuHtml(v.text, ctx)}</p>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin:16px 0">
      ${rows.map(([label, val], i) => `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #eee;${i === rows.length - 1 ? 'font-weight:700' : ''}">${label}</td>
          <td style="padding:8px;border-bottom:1px solid #eee;text-align:right;${i === rows.length - 1 ? 'font-weight:900;font-size:13px' : ''}">${val}</td>
        </tr>`).join('')}
    </table>
    ${v.schluss ? `<p style="font-size:11px;line-height:1.6">${textZuHtml(v.schluss, ctx)}</p>` : ''}
    <p style="font-size:11px;line-height:1.6;margin-top:14px">${textZuHtml(v.gruss, ctx)}<br/><strong>${escapeHtml(settings.company_name || '')}</strong></p>
    ${renderFooter(settings)}
  `;
}

/** Kurumsal Mahnung PDF'i indirir. */
export async function downloadMahnungPdf(m) {
  const settings = await fetchPdfSettings();
  await downloadPdfFromHtml(
    wrapPdfDocument(buildMahnungBody(m, settings), `${m.level_name} ${m.invoice_no || ''}`),
    `${m.level_name.replace(/[.\s]/g, '_')}_${m.invoice_no || m.id}`
  );
}

/** E-Mail düz metni. */
function emailBody(m, settings) {
  const v = vorlageFuer(m, settings);
  const ctx = mahnungKontext(m, settings);
  const plain = (t) => renderPlatzhalter(t, ctx).replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  const due = ctx['mahnung.frist_datum'];
  return [
    plain(v.anrede),
    '',
    plain(v.text),
    '',
    `Offener Rechnungsbetrag: ${eur(m.open_amount)}`,
    ...(Number(m.total_fees) > 0 ? [`Mahngebühren: ${eur(m.total_fees)}`] : []),
    `Zu zahlender Gesamtbetrag: ${eur(m.total_due)}`,
    ...(due ? [`Zahlbar bis: ${due}`] : []),
    '',
    ...(v.schluss ? [plain(v.schluss), ''] : []),
    plain(v.gruss),
    settings.company_name || 'InMotion Handel',
  ].join('\n');
}

/**
 * Mahnung'u e-posta ile gönderir (SendGrid entegrasyonu; anahtar yoksa
 * simülasyon). Başarıda kayda gönderim damgası işlenir.
 */
export async function sendMahnungEmail(m) {
  const to = m.partner_email;
  if (!to) throw new Error('Keine E-Mail-Adresse beim Kunden hinterlegt.');
  const settings = await fetchPdfSettings();
  const subject = `${m.level_name} — Rechnung ${m.invoice_no || ''}`;
  await base44.integrations.Core.SendEmail({
    to,
    subject,
    body: emailBody(m, settings),
  });
  await markMahnungEmailSent(m.id, to);
  return to;
}
